/**
 * API keys.
 *
 * A screen of its own rather than a card at the bottom of Settings, because everything on it is a
 * live credential and the two things somebody comes here to do — issue one, and kill one — should
 * not be reachable by accident while scrolling past the invoice prefix.
 *
 * The whole screen is arranged around one fact: **a key is shown once and never again.** So the
 * warning comes before the key is created rather than after, the key appears in a panel that has
 * to be dismissed deliberately, and the list underneath shows four characters and a name — which
 * is everything needed to revoke the right one and nothing that could be used.
 *
 * Read-only is the default on the form, and it is the default because a key gets pasted into a
 * script and forgotten. Write access is a deliberate second choice with its consequence spelt out
 * beside it.
 *
 * **A key is a request before it is a credential.** Unless moderation has been switched off
 * platform-wide, a new key waits to be approved and authenticates nothing until it is. That is said
 * *before* the button is pressed, again on the panel that reveals the secret, and again on the row —
 * three times, because the failure this screen exists to prevent is somebody pasting a key into a
 * deployment, watching it be refused, and spending an afternoon on their own configuration. The
 * screen must never imply a key works.
 */

import { useState, type SVGProps } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  API_KEY_LIFETIMES,
  API_KEY_SCOPE_COPY,
  API_KEY_STATUS_COPY,
  type ApiKeyScope,
  type ApiKeyStatus,
} from "@rentorng/core";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { useFormat } from "@/lib/format";
import { IconCheck, IconCopy, IconTrash } from "@/components/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  Table,
} from "@/components/ui";

/**
 * A key.
 *
 * Drawn in the same 24-grid, 1.75-stroke, rounded-cap style as the rest of `components/icons.tsx`
 * and kept here rather than added to that file, which this change does not own. If a second screen
 * ever needs it, it belongs there.
 */
const IconKey = ({ size = 18, ...rest }: SVGProps<SVGSVGElement> & { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...rest}
  >
    <circle cx="7.5" cy="12" r="4" />
    <path d="M11.5 12H21" />
    <path d="M18 12v3.2" />
    <path d="M14.6 12v2.4" />
  </svg>
);

/** The two sentences the API composes about a key that is not live. Null for one that works. */
interface ApprovalNotice {
  headline: string;
  detail: string;
}

interface KeyRow {
  id: string;
  name: string;
  hint: string;
  scope: ApiKeyScope;
  role: string;
  status: ApiKeyStatus;
  notice: ApprovalNotice | null;
  approvalState: "pending" | "approved" | "declined";
  approvalNote: string | null;
  approvalDecidedAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  createdBy: string | null;
}

interface KeyList {
  included: boolean;
  needed: string | null;
  /** Whether a key created now would have to be approved before it works. */
  moderated: boolean;
  keys: KeyRow[];
}

/**
 * A tone for each state, and the words from `@rentorng/core`.
 *
 * The labels are not written here: `API_KEY_STATUS_COPY` is the one definition, so this screen, the
 * console and the API's own refusal cannot end up calling the same state three different things.
 */
const STATUS_TONE: Record<ApiKeyStatus, "success" | "muted" | "warn" | "info" | "danger"> = {
  active: "success",
  revoked: "muted",
  expired: "warn",
  pending: "info",
  declined: "danger",
};

export function ApiKeysPage() {
  const fmt = useFormat();
  const [issued, setIssued] = useState<{ name: string; key: string; notice: ApprovalNotice | null } | null>(null);
  const [copied, setCopied] = useState(false);
  const [name, setName] = useState("");
  const [scope, setScope] = useState<ApiKeyScope>("read");
  const [lifetime, setLifetime] = useState("90");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["api-keys"],
    queryFn: () => api.get<KeyList>("/settings/api-keys"),
  });

  const create = useApiMutation({
    mutationFn: (input: { name: string; scope: ApiKeyScope; expiresInDays: number | null }) =>
      api.post<{ name: string; key: string; notice: ApprovalNotice | null }>("/settings/api-keys", input),
    invalidates: [["api-keys"]],
    onSuccess: (result) => {
      /* The notice travels with the response rather than being inferred from the list's
         `moderated`, so the panel describes the key that was actually just made — a switch flipped
         in the console between the page loading and the button being pressed does not make this
         panel lie. */
      setIssued({ name: result.name, key: result.key, notice: result.notice ?? null });
      setCopied(false);
      setName("");
      setScope("read");
      void refetch();
    },
  });

  const revoke = useApiMutation({
    mutationFn: (id: string) => api.delete(`/settings/api-keys/${id}`),
    invalidates: [["api-keys"]],
    onSuccess: () => void refetch(),
  });

  const copy = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.key);
      setCopied(true);
    } catch {
      /* A browser that refuses the clipboard is not a failure worth a message — the key is on the
         screen and can be selected. Saying "could not copy" over the top of it only gets in the
         way of the one chance to take it. */
    }
  };

  const keys = data?.keys ?? [];
  const live = keys.filter((key) => key.status === "active").length;
  /* Counted separately and said separately. "3 keys in use" while two of them are waiting for
     approval is the sentence that sends somebody to debug a script that was never going to work. */
  const waiting = keys.filter((key) => key.status === "pending").length;

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="API keys"
        back={{ to: "/app/settings", label: "Settings" }}
        /* The live figure, which is what the subtitle is for. The explanation goes below it. */
        subtitle={
          (live === 1 ? "1 key in use." : `${live} keys in use.`) +
          (waiting ? ` ${waiting === 1 ? "1 key is" : `${waiting} keys are`} waiting to be approved.` : "")
        }
        description={
          "A key lets a system you run — a website, a spreadsheet, an accounts package — work with " +
          "your records without a person signing in. It acts only inside your account, and only as " +
          "far as the access you give it. A key is shown once, when it is created, and never again."
        }
      />

      {data && !data.included ? (
        <div className="mb-5">
          <Alert tone="warn" title="The API is not included on your current plan.">
            It is available from {data.needed ?? "a higher plan"} upwards. Keys already issued are listed below and
            are refused until the plan is raised.
          </Alert>
        </div>
      ) : null}

      {/* Said before the button is pressed, not after. Somebody who knows the key will not work
          straight away does not spend an afternoon checking their own configuration. */}
      {data?.moderated ? (
        <div className="mb-5">
          <Alert tone="info" title="A new key is checked before it works.">
            Keys are approved by us before they authenticate anything. Create the key and store it as usual — it
            starts working as soon as it has been looked at, and nothing further is needed from you. You will see its
            standing in the list below.
          </Alert>
        </div>
      ) : null}

      {issued ? (
        <div className="mb-5">
          <Card>
            <CardTitle
              action={
                <span className="flex flex-wrap items-center gap-1.5">
                  {issued.notice ? <Badge tone="info">Awaiting approval</Badge> : null}
                  <Badge tone="warn">Shown once</Badge>
                </span>
              }
            >
              Your new key
            </CardTitle>
            <p className="mb-3 text-sm text-ink-600">
              Copy it now and store it wherever the key is going to be used. It is held here only as a
              one-way hash, so this is the only time it can be shown — if it is lost, the only remedy is to
              revoke it and issue another.
            </p>
            {/* The words come from the API, which is the same sentence it will answer with if the
                key is presented before it is approved. Two copies of that sentence would drift. */}
            {issued.notice ? (
              <div className="mb-3">
                <Alert tone="info" title={issued.notice.headline}>
                  {issued.notice.detail}
                </Alert>
              </div>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <code className="min-w-0 flex-1 overflow-x-auto rounded-[var(--radius-control)] bg-ink-100 px-3 py-2 font-mono text-xs text-ink-900">
                {issued.key}
              </code>
              <Button variant="secondary" onClick={() => void copy()} aria-label="Copy the key">
                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                <span className="ml-1.5">{copied ? "Copied" : "Copy"}</span>
              </Button>
              <Button variant="ghost" onClick={() => setIssued(null)}>
                Done
              </Button>
            </div>
          </Card>
        </div>
      ) : null}

      <div className="mb-5">
        <Card>
          <CardTitle>Create a key</CardTitle>

          {create.errorMessage ? (
            <div className="mb-3">
              <Alert tone="danger">{create.errorMessage}</Alert>
            </div>
          ) : null}

          <form
            className="grid gap-4 sm:grid-cols-3"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate({
                name: name.trim(),
                scope,
                expiresInDays: lifetime === "none" ? null : Number(lifetime),
              });
            }}
          >
            <Field
              label="What is it for"
              htmlFor="api-key-name"
              required
              hint="You will not be able to tell two keys apart by anything else."
              error={create.fieldErrors.name}
            >
              <Input
                id="api-key-name"
                name="name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Website vacancy feed"
                maxLength={80}
              />
            </Field>

            <Field label="Access" htmlFor="api-key-scope" hint={API_KEY_SCOPE_COPY[scope].detail}>
              <Select
                id="api-key-scope"
                name="scope"
                value={scope}
                onChange={(event) => setScope(event.target.value as ApiKeyScope)}
              >
                <option value="read">{API_KEY_SCOPE_COPY.read.label}</option>
                <option value="write">{API_KEY_SCOPE_COPY.write.label}</option>
              </Select>
            </Field>

            <Field
              label="Ends"
              htmlFor="api-key-lifetime"
              hint="A key with an end date is one you cannot forget about."
            >
              <Select
                id="api-key-lifetime"
                name="lifetime"
                value={lifetime}
                onChange={(event) => setLifetime(event.target.value)}
              >
                {API_KEY_LIFETIMES.map((choice) => (
                  <option key={choice.label} value={choice.days === null ? "none" : String(choice.days)}>
                    {choice.label}
                  </option>
                ))}
              </Select>
            </Field>

            <div className="sm:col-span-3">
              <Button type="submit" disabled={create.isPending || name.trim().length < 2}>
                <IconKey size={16} />
                <span className="ml-1.5">{create.isPending ? "Creating…" : "Create key"}</span>
              </Button>
            </div>
          </form>
        </Card>
      </div>

      <Card padded={false}>
        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} retry={() => refetch()} />
        ) : keys.length === 0 ? (
          <EmptyState
            title="No keys yet."
            hint="Create one when you have software that needs to read or update your records on its own."
          />
        ) : (
          <Table
            head={
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Access</th>
                <th>Status</th>
                <th>Last used</th>
                <th>Created</th>
                <th aria-label="Revoke" />
              </tr>
            }
          >
            {keys.map((key) => (
              <tr key={key.id}>
                <td>
                  <span className="flex items-center gap-2 font-medium text-ink-900">
                    <IconKey size={15} className="text-ink-400" />
                    {key.name}
                  </span>
                  {key.createdBy ? <span className="block text-xs text-ink-500">by {key.createdBy}</span> : null}
                </td>
                <td className="whitespace-nowrap font-mono text-xs text-ink-500">····{key.hint}</td>
                <td>
                  <Badge tone={key.scope === "write" ? "warn" : "info"}>{API_KEY_SCOPE_COPY[key.scope].label}</Badge>
                </td>
                <td>
                  <Badge tone={STATUS_TONE[key.status]}>{API_KEY_STATUS_COPY[key.status].label}</Badge>
                  {key.status === "active" && key.expiresAt ? (
                    <span className="block text-xs text-ink-500">until {fmt.dateTime(key.expiresAt)}</span>
                  ) : null}
                  {/*
                    The headline on the row and the reviewer's own words beneath it — not the whole
                    paragraph, which turned this column into a wall of text three lines wide. The
                    long form of the same thing is in the notice above the table and in the API's
                    own refusal; what belongs *here* is which key it is about and, for a refusal,
                    the sentence the agency has to act on.
                  */}
                  {key.notice ? (
                    <span className="mt-1 block text-xs font-medium text-ink-700">{key.notice.headline}</span>
                  ) : null}
                  {key.status === "pending" ? (
                    <span className="block text-xs text-ink-500">It works as soon as it has been approved.</span>
                  ) : null}
                  {key.status === "declined" ? (
                    <span className="block text-xs text-ink-500">
                      {key.approvalNote ? `${key.approvalNote} ` : ""}It cannot be brought back — create another.
                    </span>
                  ) : null}
                </td>
                {/* The column that answers "can this one go?". A key that has never been used, or
                    has not been used in a year, is the one to revoke first. */}
                <td className="whitespace-nowrap text-ink-500">
                  {key.lastUsedAt ? fmt.dateTime(key.lastUsedAt) : "Never used"}
                </td>
                <td className="whitespace-nowrap text-ink-500">{fmt.dateTime(key.createdAt)}</td>
                <td className="text-right">
                  {/* A key that was not approved is finished — there is nothing left to withdraw,
                      and offering the button would suggest it might otherwise still be working. */}
                  {key.status === "revoked" || key.status === "declined" ? null : (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={revoke.isPending}
                      onClick={() => {
                        /* Confirmed, because it cannot be undone and it stops whatever is using the
                           key on its very next request. */
                        if (window.confirm(`Revoke “${key.name}”? Anything using it will stop working at once.`)) {
                          revoke.mutate(key.id);
                        }
                      }}
                      aria-label={`Revoke ${key.name}`}
                    >
                      <IconTrash size={16} />
                      <span className="ml-1.5">Revoke</span>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <p className="mt-4 text-xs text-ink-500">
        Send a key as <code className="font-mono">Authorization: Bearer …</code> on any request to the API. A key
        never carries billing, team or settings access, and it can never create another key. A key that has not been
        approved is refused with a message saying so, rather than being treated as invalid.
      </p>
    </div>
  );
}
